import React from 'react';
import { motion } from 'framer-motion';

/**
 * Brand-only case study section.
 * Renders a two-column layout: quote + headline stat on the left,
 * 2×2 metric grid on the right.
 * Only shown when view === 'brand' — the parent is responsible for conditionally rendering this.
 */
const CaseStudy: React.FC = () => {
  const metrics = [
    { value: '$0.82', label: 'Effective CPM', gradient: 'cv-text-gradient-green' },
    { value: '47', label: 'Active Creators', gradient: 'cv-text-gradient' },
    { value: '8.4x', label: 'ROAS', gradient: 'cv-text-gradient-green' },
    { value: '156', label: 'Videos Created', gradient: 'cv-text-gradient' },
  ];

  return (
    <section
      className="relative"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-0">
          <div
            className="inline-flex items-center gap-2 mb-4"
            style={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--cv-accent)',
            }}
          >
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
            CASE STUDY
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold tracking-tight mb-14"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.02em',
            }}
          >
            Real results from <span className="cv-text-gradient">real brands</span>
          </motion.h2>
        </div>

        {/* Case study card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="cv-case-study-inner relative overflow-hidden"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '64px',
            alignItems: 'center',
            padding: '64px',
            background: 'var(--cv-surface)',
            border: '1px solid var(--cv-border)',
            borderRadius: 'var(--radius-xl)',
          }}
        >
          {/* Left — headline stat + quote */}
          <div>
            <div className="mb-8">
              <div
                className="cv-text-gradient font-extrabold tracking-tight"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-3xl)',
                  letterSpacing: '-0.02em',
                }}
              >
                12.4M
              </div>
              <div
                className="mt-1"
                style={{ fontSize: 'var(--text-sm)', color: 'var(--cv-text-muted)' }}
              >
                Total Views in 30 Days
              </div>
            </div>

            <blockquote
              style={{
                fontSize: 'var(--text-lg)',
                fontStyle: 'italic',
                lineHeight: 1.6,
                color: 'var(--cv-text-muted)',
                borderLeft: '3px solid var(--cv-accent)',
                paddingLeft: '24px',
              }}
            >
              "CreatorVault completely changed how we approach UGC. We went from managing dozens of
              spreadsheets to having everything in one dashboard. The CPM model means we only pay
              for real results."
            </blockquote>

            <div
              className="mt-4"
              style={{
                fontSize: 'var(--text-sm)',
                fontStyle: 'normal',
                color: 'var(--cv-text-faint)',
              }}
            >
              — Marketing Director, DTC Beauty Brand
            </div>
          </div>

          {/* Right — 2×2 metrics */}
          <div
            className="grid grid-cols-2 gap-6"
          >
            {metrics.map((m) => (
              <div
                key={m.label}
                className="p-6"
                style={{
                  background: 'var(--cv-surface-2)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--cv-border)',
                }}
              >
                <div
                  className={`${m.gradient} font-extrabold`}
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-xl)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {m.value}
                </div>
                <div
                  className="mt-1"
                  style={{ fontSize: '13px', color: 'var(--cv-text-muted)' }}
                >
                  {m.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudy;
